import axios from "axios";
import {ElMessage} from "element-plus";
import router from "@/router";
import {i18n} from "@/lang/index";
import {getToken} from "./auth";
import store from "@/store/index";
import sessionStore from "@/utils/sessionStore";

let instance = axios.create();

instance.defaults.baseURL = process.env.VUE_APP_BASE_API;
instance.defaults.timeout = 300000;
instance.defaults.headers["Content-Type"] = "application/json; charset=utf-8;";

let requestSeed = 0;
const pendingRequest = new Map();
const inflightPromise = new Map();

let teamIdInitPromise = null;
let authExpiredHandlingPromise = null;

export function resetRequestRuntimeCache() {
    try {
        cancelAllPendingRequests();
    } catch (e) {
    }
    try {
        inflightPromise.clear();
    } catch (e) {
    }
    teamIdInitPromise = null;
    authExpiredHandlingPromise = null;
}

const normalizeTeamId = (raw) => {
    if (raw === undefined || raw === null) return '';
    const s = String(raw);
    if (!s || s === '0' || s === 'undefined' || s === 'null') return '';
    return s;
};

const getCurrentTeamId = () => {
    try {
        const fromStore = normalizeTeamId(store?.state?.email?.teamID);
        if (fromStore) return fromStore;
    } catch (e) {
    }
    try {
        const fromSession = normalizeTeamId(sessionStore.get('teamId'));
        if (fromSession) return fromSession;
    } catch (e) {
    }
    return '';
};

const normalizeFailMessage = (msg) => {
    return String(msg ?? '').trim();
};

const normalizeCompactFailMessage = (msg) => {
    return normalizeFailMessage(msg).replace(/\s+/g, '').toLowerCase();
};

const shouldSuppressBusinessFail = (msg) => {
    const compact = normalizeCompactFailMessage(msg);
    return (
        compact === '' ||
        compact === 'null' ||
        compact === '操作失败,null' ||
        compact === '操作失败，null' ||
        compact === '团队id不能为空' ||
        compact.includes('用户id不能为空') ||
        compact.includes('userid不能为空')
    );
};

const ensureTeamIdReady = async () => {
    const existing = getCurrentTeamId();
    if (existing) return existing;

    if (!teamIdInitPromise) {
        teamIdInitPromise = Promise.resolve()
            .then(() => store.dispatch('email/ensureTeamListReady'))
            .catch(() => {
            })
            .finally(() => {
                teamIdInitPromise = null;
            });
    }
    await teamIdInitPromise;
    return getCurrentTeamId();
};

const teamIdRequiredRequestMatchers = [
    '/front/mailCustomer/getCustomerCreateList',
    '/front/mailCustomer/getCustomerProductSelectData',
    '/front/mailCustomer/getCustomerOriginSelectData',
    '/front/index/statisticsCurrentTeamData',
    '/front/index/statisticsAllTeamData',
    '/front/mailFrontUser/queryTeamManagerByTeamId',
    '/front/mail/config/getUserMail',
];

const shouldHydrateTeamIdForRequest = (url) => {
    const targetUrl = String(url || '');
    if (!targetUrl) return false;
    return teamIdRequiredRequestMatchers.some((matcher) => targetUrl.includes(matcher));
};

const normalizeMethod = (method) => String(method || 'get').toUpperCase();

const getHeaderValue = (headers, key) => {
    if (!headers || !key) return '';
    const targetKey = String(key).toLowerCase();
    if (typeof headers.get === 'function') {
        return headers.get(key) || headers.get(targetKey) || '';
    }
    for (const headerKey of Object.keys(headers)) {
        if (String(headerKey).toLowerCase() === targetKey) {
            return headers[headerKey];
        }
    }
    return '';
};

const sortSerializableValue = (value) => {
    if (Array.isArray(value)) {
        return value.map((item) => sortSerializableValue(item));
    }
    if (value && typeof value === 'object') {
        if (value instanceof Date) {
            return value.toISOString();
        }
        if (typeof File !== 'undefined' && value instanceof File) {
            return {
                __type: 'File',
                name: value.name,
                size: value.size,
                type: value.type,
                lastModified: value.lastModified,
            };
        }
        if (typeof Blob !== 'undefined' && value instanceof Blob) {
            return {
                __type: 'Blob',
                size: value.size,
                type: value.type,
            };
        }
        return Object.keys(value).sort().reduce((acc, currentKey) => {
            acc[currentKey] = sortSerializableValue(value[currentKey]);
            return acc;
        }, {});
    }
    return value;
};

const serializeConfigValue = (value) => {
    if (value === undefined || value === null) return '';
    if (typeof FormData !== 'undefined' && value instanceof FormData) {
        const entries = [];
        value.forEach((entryValue, entryKey) => {
            entries.push([entryKey, sortSerializableValue(entryValue)]);
        });
        return JSON.stringify(entries);
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed && ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']')))) {
            try {
                return JSON.stringify(sortSerializableValue(JSON.parse(trimmed)));
            } catch (e) {
            }
        }
        return value;
    }
    if (typeof value === 'object') {
        return JSON.stringify(sortSerializableValue(value));
    }
    return String(value);
};

const resolveRequestUrl = (config) => {
    const baseURL = config?.baseURL || instance.defaults.baseURL || '';
    const url = String(config?.url || '');
    return `${baseURL}${url}`;
};

const createRequestId = () => {
    requestSeed += 1;
    return `req_${Date.now()}_${requestSeed}`;
};

function ensureRequestMeta(config) {
    if (!config.__requestId) {
        config.__requestId = createRequestId();
    }
    return config.__requestId;
}

function isUploadLikeRequest(config = {}) {
    const url = String(config?.url || '').toLowerCase();
    const contentType = String(getHeaderValue(config?.headers, 'Content-Type') || '').toLowerCase();
    return (
        (typeof FormData !== 'undefined' && config?.data instanceof FormData) ||
        contentType.includes('multipart/form-data') ||
        url.includes('/upload')
    );
}

function extractRequestTeamId(config = {}) {
    const teamIdFromParams = normalizeTeamId(config?.params?.teamId);
    if (teamIdFromParams) return teamIdFromParams;

    const data = config?.data;
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
        return normalizeTeamId(data.get('teamId'));
    }
    if (data && typeof data === 'object') {
        return normalizeTeamId(data.teamId);
    }
    if (typeof data === 'string') {
        try {
            const parsed = JSON.parse(data);
            return normalizeTeamId(parsed?.teamId);
        } catch (e) {
        }
    }
    return '';
}

function buildRequestIdentity(config = {}) {
    const method = normalizeMethod(config?.method);
    const url = resolveRequestUrl(config);
    const token = getHeaderValue(config?.headers, 'X-Access-Token') || getToken() || '';
    const params = serializeConfigValue(config?.params);
    const data = serializeConfigValue(config?.data);
    const teamId = shouldHydrateTeamIdForRequest(config?.url)
        ? extractRequestTeamId(config) || getCurrentTeamId()
        : '';
    return [method, url, params, data, token, teamId].join('&');
}

const getCacheKey = (config) => buildRequestIdentity(config);

function buildInflightKey(config = {}) {
    return buildRequestIdentity(config);
}

function bindAbortController(config) {
    const controller = new AbortController();
    const externalSignal = config?.signal;
    let unbindExternalAbort = null;

    if (externalSignal && externalSignal !== controller.signal) {
        if (externalSignal.aborted) {
            controller.abort(externalSignal.reason);
        } else if (typeof externalSignal.addEventListener === 'function') {
            const relayAbort = () => controller.abort(externalSignal.reason);
            externalSignal.addEventListener('abort', relayAbort, {once: true});
            unbindExternalAbort = () => {
                try {
                    externalSignal.removeEventListener('abort', relayAbort);
                } catch (e) {
                }
            };
        }
    }

    config.signal = controller.signal;
    return {
        controller,
        cleanup() {
            unbindExternalAbort?.();
        },
    };
}

function registerPendingRequest(config) {
    const requestId = ensureRequestMeta(config);
    if (pendingRequest.has(requestId)) return pendingRequest.get(requestId);
    const binding = bindAbortController(config);
    pendingRequest.set(requestId, binding);
    return binding;
}

function unregisterPendingRequest(configOrRequestId, {abort = false} = {}) {
    const requestId = typeof configOrRequestId === 'object' ? configOrRequestId?.__requestId : configOrRequestId;
    if (!requestId || !pendingRequest.has(requestId)) return;

    const binding = pendingRequest.get(requestId);
    pendingRequest.delete(requestId);

    try {
        binding?.cleanup?.();
    } catch (e) {
    }

    if (abort) {
        try {
            binding?.controller?.abort?.(requestId);
        } catch (e) {
        }
    }
}

function cancelAllPendingRequests() {
    for (const requestId of [...pendingRequest.keys()]) {
        unregisterPendingRequest(requestId, {abort: true});
    }
}

const isTokenExpiredMessage = (message) => {
    return normalizeFailMessage(message) === 'Token失效，请重新登录';
};

const shouldHandleAuthExpired = (config) => {
    return config?.skipAuthExpiredHandler !== true;
};

const redirectToLogin = () => {
    try {
        const currentRoute = router.currentRoute?.value;
        if (currentRoute?.name === 'Login') return;
    } catch (e) {
    }
    try {
        router.push({name: 'Login'});
    } catch (e) {
    }
};

const handleAuthExpired = async ({message = '', shouldNotify = true} = {}) => {
    if (authExpiredHandlingPromise) return authExpiredHandlingPromise;

    authExpiredHandlingPromise = (async () => {
        cancelAllPendingRequests();

        const hasToken = Boolean(store.getters.token || getToken());
        if (hasToken) {
            if (shouldNotify) {
                const text = normalizeFailMessage(message) || i18n.global.t('common.sss8');
                ElMessage.error(text);
            }
            try {
                await store.dispatch('user/UserQuit', {
                    skipLogoutRequest: true,
                });
            } catch (e) {
            }
            try {
                await store.dispatch('chat/logout');
            } catch (e) {
            }
            return;
        }

        if (shouldNotify) {
            const text = normalizeFailMessage(message) || i18n.global.t('common.sss8');
            ElMessage.error(text);
        }
        redirectToLogin();
    })().finally(() => {
        authExpiredHandlingPromise = null;
    });

    return authExpiredHandlingPromise;
};

instance.interceptors.request.use(async (config) => {
    registerPendingRequest(config);

    /**
     * 取消当前接口请求 并从pendingRequest中删除
     *    封装邮件取消功能
     * let { data} = await this.$api.xxx({}, {
     *        cancelRequest: cancel => {
     *            cancel()
     *        }
     *    })
     */
    config.cancelRequest && config.cancelRequest(function cancel() {
        unregisterPendingRequest(config, {abort: true});
    });

    config.headers = config.headers || {};
    getToken() && (config.headers['X-Access-Token'] = getToken());

    // 为切团队依赖的关键接口补齐 teamId，避免页面长时间挂起后恢复时出现 teamId 初始化竞态
    try {
        const url = String(config?.url || '');
        if (shouldHydrateTeamIdForRequest(url)) {
            const method = String(config?.method || 'get').toLowerCase();
            const teamIdFromParams = normalizeTeamId(config?.params?.teamId);
            const teamIdFromData = normalizeTeamId(config?.data?.teamId);
            let tid = teamIdFromParams || teamIdFromData || getCurrentTeamId();
            if (!tid) {
                tid = await ensureTeamIdReady();
            }
            if (tid) {
                if (method === 'get' || method === 'delete') {
                    config.params = {...(config.params || {}), teamId: tid};
                } else if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
                    if (!normalizeTeamId(config.data.get('teamId'))) {
                        config.data.append('teamId', tid);
                    }
                } else if (config.data && typeof config.data === 'string') {
                    try {
                        const parsed = JSON.parse(config.data);
                        config.data = JSON.stringify({...parsed, teamId: tid});
                    } catch (e) {
                        // ignore
                    }
                } else {
                    config.data = {...(config.data || {}), teamId: tid};
                }
            }
        }
    } catch (e) {
    }
    if (config.clearCache) {
        try {
            const controller = navigator.serviceWorker?.controller;
            if (controller && typeof controller.postMessage === 'function') {
                controller.postMessage(`clearCache:${getCacheKey(config)}`);
            }
        } catch (e) {
        }
    }

    return config;
}, (error) => {
    return Promise.reject(error);
});

instance.interceptors.response.use((response) => {
    unregisterPendingRequest(response.config);
    const {code, message} = response.data;
    if (response.status === 200) {
        if (code === 500) {
            if (shouldSuppressBusinessFail(message)) {
                return Promise.resolve({
                    ...response.data,
                    __silencedBusinessFail: true,
                });
            }
            try {
                const url = String(response?.config?.url || '');
                const msg = String(message || '');
                if (url.includes('/whatsApp/user/getUserInfoBySession') && msg.includes('未登录')) {
                    return Promise.resolve(response.data);
                }
            } catch (e) {
            }
            if (isTokenExpiredMessage(message) && shouldHandleAuthExpired(response?.config)) {
                return handleAuthExpired({
                    message,
                    shouldNotify: false,
                }).then(() => Promise.reject(message));
            }
            if (response?.config?.errorPop === false) {
                return Promise.resolve(response.data);
            }
            if (!isTokenExpiredMessage(message) && message !== '网络错误' && !shouldSuppressBusinessFail(message)) {
                if (!message?.includes('Size')) {
                    ElMessage.error(message);
                }
                return Promise.reject(message);
            }
            return Promise.reject(message);
        }
        return Promise.resolve(response.data);
    }
    return Promise.reject(response);
}, (error) => {
    const requestConfig = error?.config;
    console.log('请求失败，请求配置信息：', requestConfig, error);
    unregisterPendingRequest(requestConfig || {});
    let $t = i18n.global.t;
    const isTimeout = /timeout of \d+ms exceeded/.test(error?.message || '');
    const isCancel = axios.isCancel(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
    const shouldPop = requestConfig?.errorPop !== false;
    const normalized = error?.response ? error.response : {};

    if (isCancel) {
        try {
            if (typeof normalized === 'object' && normalized) {
                normalized.__CANCEL__ = true;
                normalized.code = normalized.code || error?.code;
                normalized.message = normalized.message || error?.message || 'Request canceled';
            }
        } catch (e) {
        }
        return Promise.reject(normalized);
    }

    if (isTimeout && shouldPop) ElMessage.error($t('common.sss6'));
    if (error?.message === 'Network Error' && error?.config?.url != '/v2/Mail/ReceiveMail') {
        unregisterPendingRequest(error?.config || {});
    }

    // 某些上传（尤其是 el-upload / XHR）在请求被服务端拒绝时，可能拿不到标准 HTTP status，
    // 会抛出类似："fail to post https://.../sys/oss/qiniu/upload 0" 的错误。
    // 这里将其归一为更友好的“文件过大”提示，避免误导用户。
    try {
        const url = String(error?.config?.url || '');
        const msg = String(error?.message || '');
        const isQiniuUpload = url.includes('/sys/oss/qiniu/upload');
        const isFailToPost = /fail to post/i.test(msg) && msg.includes('qiniu/upload');
        if (shouldPop && (isQiniuUpload || isFailToPost) && !error?.response) {
            ElMessage.error('上传失败：文件过大，请压缩后重试或更换更小的文件。');
            return Promise.reject('上传失败：文件过大，请压缩后重试或更换更小的文件。');
        }
    } catch (e) {
    }

    if (error.response && error.response.status) {
        const responseData = error.response.data || {};
        if (shouldSuppressBusinessFail(responseData.message)) {
            return Promise.resolve({
                ...responseData,
                __silencedBusinessFail: true,
            });
        }
        switch (error.response.status) {
            case 401:
                if (!shouldHandleAuthExpired(requestConfig)) {
                    break;
                }
                return handleAuthExpired({
                    message: $t('common.sss8'),
                    shouldNotify: shouldPop,
                }).then(() => Promise.reject(normalized));
            case 403:
                if (!shouldHandleAuthExpired(requestConfig)) {
                    break;
                }
                return handleAuthExpired({
                    message: $t('common.sss9'),
                    shouldNotify: shouldPop,
                }).then(() => Promise.reject(normalized));
            case 413: {
                try {
                    const url = String(error?.config?.url || '');
                    const isQiniuUpload = url.includes('/sys/oss/qiniu/upload');
                    const msg = isQiniuUpload
                        ? '上传失败：文件过大，请压缩后重试或更换更小的文件。'
                        : '请求失败：上传内容过大，请缩小文件后重试。';
                    if (shouldPop) ElMessage.error(msg);
                } catch (e) {
                    if (shouldPop) ElMessage.error('请求失败：上传内容过大，请缩小文件后重试。');
                }
                break;
            }
            case 404:
                if (shouldPop) ElMessage.error($t('common.sss10'));
                break;
            case 500:
                if (!isTokenExpiredMessage(error.response.data.message) && error.response.data.message !== '网络错误' && !shouldSuppressBusinessFail(error.response.data.message)) {
                    if (!error.response.data.message?.includes('Size')) {
                        if (shouldPop) ElMessage.error(error.response.data.message);
                    }
                } else if (isTokenExpiredMessage(error.response.data.message) && shouldHandleAuthExpired(requestConfig)) {
                    return handleAuthExpired({
                        message: error.response.data.message,
                        shouldNotify: false,
                    }).then(() => Promise.reject(normalized));
                }
                break;
            default:
                if (shouldPop) ElMessage.error(error.response.data.message);
        }
    }
    try {
        if (typeof normalized === 'object' && normalized) {
            normalized.__CANCEL__ = false;
            normalized.__TIMEOUT__ = isTimeout;
            normalized.code = normalized.code || error?.code;
            normalized.message = normalized.message || error?.message;
        }
    } catch (e) {
    }
    return Promise.reject(normalized);
});

/**
 * 取消当前接口请求 并从pendingRequest中删除
 *    封装邮件取消功能
 * let { data} = await this.$api.xxx({}, {
 *        cancelRequest: cancel => {
 *            cancel()
 *        }
 *    })
 */

function shouldSkipDedupe(config = {}) {
    if (config?.dedupe === false) return true;
    if (typeof config?.cancelRequest === 'function') return true;
    return isUploadLikeRequest(config);
}

function dispatchRequest(config = {}) {
    const requestConfig = {
        errorPop: true,
        clearCache: false,
        ...config,
    };

    if (shouldSkipDedupe(requestConfig)) {
        return instance(requestConfig);
    }

    const inflightKey = buildInflightKey(requestConfig);
    if (inflightPromise.has(inflightKey)) {
        return inflightPromise.get(inflightKey);
    }

    const requestPromise = instance(requestConfig).finally(() => {
        inflightPromise.delete(inflightKey);
    });
    inflightPromise.set(inflightKey, requestPromise);
    return requestPromise;
}

function isPlainObject(value) {
    return Object.prototype.toString.call(value) === '[object Object]';
}

function hasOwnKey(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
}

function isRequestConfigLike(value) {
    if (!isPlainObject(value)) return false;
    const requestConfigKeys = [
        'headers',
        'responseType',
        'responseEncoding',
        'errorPop',
        'clearCache',
        'cancelRequest',
        'dedupe',
        'signal',
        'baseURL',
        'timeout',
        'timeoutErrorMessage',
        'paramsSerializer',
        'withCredentials',
        'auth',
        'adapter',
        'proxy',
        'httpAgent',
        'httpsAgent',
        'onUploadProgress',
        'onDownloadProgress',
        'maxContentLength',
        'maxBodyLength',
        'validateStatus',
        'decompress',
        'transitional',
        'transformRequest',
        'transformResponse',
    ];
    return requestConfigKeys.some((key) => hasOwnKey(value, key));
}

function resolveDeleteArgs(paramsOrConfig = {}, config = {}) {
    if (config && Object.keys(config).length > 0) {
        return {
            params: paramsOrConfig,
            config,
        };
    }

    if (!isPlainObject(paramsOrConfig)) {
        return {
            params: paramsOrConfig || {},
            config: {},
        };
    }

    if (hasOwnKey(paramsOrConfig, 'params')) {
        const {params = {}, ...restConfig} = paramsOrConfig;
        return {
            params,
            config: restConfig,
        };
    }

    if (isRequestConfigLike(paramsOrConfig)) {
        return {
            params: {},
            config: paramsOrConfig,
        };
    }

    return {
        params: paramsOrConfig,
        config: {},
    };
}

function appendQueryParam(searchParams, key, value) {
    if (value === undefined) return;

    if (Array.isArray(value)) {
        value.forEach((item) => appendQueryParam(searchParams, key, item));
        return;
    }

    if (value instanceof Date) {
        searchParams.append(key, value.toISOString());
        return;
    }

    if (value === null) {
        searchParams.append(key, '');
        return;
    }

    searchParams.append(key, String(value));
}

function normalizeDeleteUrlAndParams(url, params = {}) {
    const rawUrl = String(url || '');
    if (!rawUrl.includes('?') || (!isPlainObject(params) && !(params instanceof URLSearchParams))) {
        return {
            url,
            params,
        };
    }

    const hashIndex = rawUrl.indexOf('#');
    const hash = hashIndex >= 0 ? rawUrl.slice(hashIndex) : '';
    const urlWithoutHash = hashIndex >= 0 ? rawUrl.slice(0, hashIndex) : rawUrl;
    const queryIndex = urlWithoutHash.indexOf('?');

    if (queryIndex < 0) {
        return {
            url,
            params,
        };
    }

    const pathname = urlWithoutHash.slice(0, queryIndex);
    const queryString = urlWithoutHash.slice(queryIndex + 1);
    const searchParams = new URLSearchParams(queryString);
    const entries = params instanceof URLSearchParams ? [...params.entries()] : Object.entries(params);

    entries.forEach(([key, value]) => {
        searchParams.delete(key);
        appendQueryParam(searchParams, key, value);
    });

    const mergedQueryString = searchParams.toString();
    return {
        url: `${pathname}${mergedQueryString ? `?${mergedQueryString}` : ''}${hash}`,
        params: {},
    };
}

let request = {
    get(url, params = {}, data = {}, config = {}) {
        const hasExplicitConfig = config && Object.keys(config).length > 0;
        const treatThirdAsConfig = !hasExplicitConfig && data && Object.keys(data).length > 0;
        const finalConfig = treatThirdAsConfig ? data : config;

        const requestConfig = {
            method: 'GET',
            params,
            url,
            ...finalConfig,
        };
        return dispatchRequest(requestConfig);
    },
    post(url, data = {}, config = {}) {
        const requestConfig = {
            method: 'POST',
            data,
            url,
            ...config,
        };
        return dispatchRequest(requestConfig);
    },
    delete(url, params = {}, config = {}) {
        const {params: resolvedParams, config: finalConfig} = resolveDeleteArgs(params, config);
        const {url: finalUrl, params: finalParams} = normalizeDeleteUrlAndParams(url, resolvedParams);
        const requestConfig = {
            method: 'DELETE',
            params: finalParams,
            url: finalUrl,
            ...finalConfig,
        };
        return dispatchRequest(requestConfig);
    },
    put(url, params = {}, data = {}, config = {}) {
        const argLength = arguments.length;
        let finalParams = {};
        let finalData = {};
        let finalConfig = {};

        if (argLength >= 4) {
            finalParams = params;
            finalData = data;
            finalConfig = config;
        } else if (argLength === 3) {
            finalData = params;
            finalConfig = data || {};
        } else if (argLength === 2) {
            if (isRequestConfigLike(params)) {
                finalConfig = params;
            } else {
                finalData = params;
            }
        }

        const requestConfig = {
            method: 'PUT',
            data: finalData,
            url,
            params: finalParams,
            ...finalConfig,
        };
        return dispatchRequest(requestConfig);
    },
};

const requestClient = function (config = {}) {
    return dispatchRequest(config);
};

requestClient.defaults = instance.defaults;
requestClient.interceptors = instance.interceptors;
requestClient.getUri = instance.getUri.bind(instance);
requestClient.request = dispatchRequest;
requestClient.get = request.get;
requestClient.post = request.post;
requestClient.delete = request.delete;
requestClient.put = request.put;
requestClient.create = instance.create?.bind(instance);

export default requestClient;
